import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { Palmtree, Ban, RotateCcw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { safeFormatDate } from '../lib/dateTime';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const LEAVE_TYPE_LABELS = {
  vacation: 'Urlaub',
  special_leave: 'Sonderurlaub',
  overtime_reduction: 'Zeitausgleich',
};

/**
 * Genehmigte Abwesenheiten der Personen in der eigenen Reichweite (ohne Krankheit).
 * Von hier aus schlagen Vorgesetzte eine Stornierung vor; die betroffene Person
 * bestätigt oder lehnt unter „Abwesenheiten“ ab.
 */
export const TeamLeavesPanel = ({ onChanged }) => {
  const { api } = useAuth();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(String(currentYear));
  const [personId, setPersonId] = useState('all');
  const [leaves, setLeaves] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [proposeLeave, setProposeLeave] = useState(null);
  const [reason, setReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const fetchLeaves = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get('/vacations/team', { params: { year: Number(year) } });
      setLeaves(res.data || []);
    } catch (error) {
      setLeaves([]);
    } finally {
      setIsLoading(false);
    }
  }, [api, year]);

  useEffect(() => {
    fetchLeaves();
  }, [fetchLeaves]);

  // Personenliste aus den geladenen Einträgen, alphabetisch
  const persons = useMemo(() => {
    const map = {};
    leaves.forEach((l) => {
      if (l.user?.id) map[l.user.id] = `${l.user.last_name}, ${l.user.first_name}`;
    });
    return Object.entries(map).sort((a, b) => a[1].localeCompare(b[1]));
  }, [leaves]);

  const visibleLeaves = personId === 'all' ? leaves : leaves.filter((l) => l.user_id === personId);

  const openPropose = (leave) => {
    setProposeLeave(leave);
    setReason('');
  };

  const handlePropose = async () => {
    if (!proposeLeave || reason.trim().length < 5) return;
    setIsSaving(true);
    try {
      await api.post(`/vacations/${proposeLeave.id}/cancel-request`, { reason: reason.trim() });
      toast.success('Stornierung vorgeschlagen – die Person muss bestätigen');
      setProposeLeave(null);
      fetchLeaves();
      if (onChanged) onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Vorschlagen der Stornierung');
    } finally {
      setIsSaving(false);
    }
  };

  const yearOptions = [currentYear - 1, currentYear, currentYear + 1].map(String);

  return (
    <div className="space-y-4" data-testid="team-leaves-panel">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
          <Palmtree className="w-5 h-5 text-teal-400" />
          Genehmigte Abwesenheiten im Team
        </h3>
        <div className="flex items-center gap-2">
          <Select value={personId} onValueChange={setPersonId}>
            <SelectTrigger className="w-[220px] bg-input border-border text-foreground" aria-label="Person">
              <SelectValue placeholder="Person" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="all">Alle Personen</SelectItem>
              {persons.map(([id, name]) => (
                <SelectItem key={id} value={id}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-[110px] bg-input border-border text-foreground" aria-label="Jahr">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              {yearOptions.map((y) => (
                <SelectItem key={y} value={y}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="h-16 rounded-lg bg-muted/30 animate-pulse" />
      ) : visibleLeaves.length === 0 ? (
        <p className="text-sm text-muted-foreground">Keine genehmigten Abwesenheiten in diesem Zeitraum.</p>
      ) : (
        <div className="card-glass divide-y divide-border">
          {visibleLeaves.map((leave) => {
            const type = leave.leave_type || leave.vacation_type || 'vacation';
            const pending = leave.cancellation_pending;
            return (
              <div key={leave.id} className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-4" data-testid={`team-leave-${leave.id}`}>
                <div className="min-w-0">
                  <p className="text-foreground font-medium">
                    {leave.user?.first_name} {leave.user?.last_name}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {LEAVE_TYPE_LABELS[type] || type} · {safeFormatDate(leave.start_date, 'd. MMM')} – {safeFormatDate(leave.end_date, 'd. MMM yyyy')} · {leave.days} Tage
                  </p>
                </div>
                {pending ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/30 self-start md:self-auto">
                    <RotateCcw className="w-3 h-3" />
                    Stornierung vorgeschlagen
                  </span>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openPropose(leave)}
                    className="border-amber-500/30 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10 self-start md:self-auto"
                    data-testid={`propose-cancel-${leave.id}`}
                  >
                    <Ban className="w-3 h-3 mr-1" />
                    Stornierung vorschlagen
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!proposeLeave} onOpenChange={(open) => { if (!open) setProposeLeave(null); }}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading">Stornierung vorschlagen</DialogTitle>
          </DialogHeader>
          {proposeLeave && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                <span className="text-foreground font-medium">{proposeLeave.user?.first_name} {proposeLeave.user?.last_name}</span>
                {' · '}
                {safeFormatDate(proposeLeave.start_date, 'd. MMM')} – {safeFormatDate(proposeLeave.end_date, 'd. MMM yyyy')}
                {' · '}
                {proposeLeave.days} Tage
              </p>
              <p className="text-sm text-muted-foreground">
                Der Urlaub bleibt genehmigt und zählt weiter, bis die Person die Stornierung bestätigt.
                Bis dahin können Sie den Vorschlag unter „Ausstehende Stornierungen“ zurückziehen.
              </p>
              <div className="space-y-2">
                <Label htmlFor="propose-cancel-reason" className="text-muted-foreground">
                  Grund <span className="text-red-600 dark:text-red-400">*</span>
                </Label>
                <Textarea
                  id="propose-cancel-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder="Warum soll der Urlaub storniert werden? (mind. 5 Zeichen)"
                  className="bg-input border-border text-foreground resize-none"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => setProposeLeave(null)}>Abbrechen</Button>
            <Button
              type="button"
              onClick={handlePropose}
              disabled={isSaving || reason.trim().length < 5}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              Vorschlag senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamLeavesPanel;